import { useCallback, useEffect, useRef, useState } from 'react';

import { format, addDays, isBefore } from 'date-fns';
import isNil from 'lodash-es/isNil';
import { CalendarIcon, ClockIcon } from 'lucide-react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useClickAway } from 'react-use';

import { BrainVersionSelector } from '@/components/atoms/BrainVersionSelector';
import DropdownList from '@/components/atoms/DropdownList/DropdownList';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { TIME_DROPDOWN_INTERVAL, timeIntervalsMaker } from '@/components/pages/BusinessHours/utils';
import { Calendar } from '@/components/ui/calendar';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { getPermissions } from '@/redux/permissions/selectors';
import { trackEvent } from '@/segment/segment';
import { FieldsetStatus, Form } from '@/shared/types/broadcast';
import { OptionBase } from '@/shared/types/common';
import { actions as permissionsActions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { delay } from '@/util/util';

import { actions } from '../../redux/actions';
import { selectBroadcast } from '../../redux/selectors';
import { SEND_NOW } from '../../utils';
import StepFieldset from '../StepFieldset/StepFieldset';

import styles from './SettingsFieldset.module.scss';

export const periodOptions = [
  { title_key: 'analytics.periods.send_now' },
  { title_key: 'analytics.periods.today', days: 0 },
  { title_key: 'analytics.periods.tomorrow', days: 1 },
  { title_key: 'analytics.periods.in_a_week', days: 7 },
  { title_key: 'analytics.periods.in_a_month', days: 30 },
  { title_key: 'analytics.periods.choose_date' }, // custom period must remain last in this array
];

export const optionsSessionTimeout = Array.from({ length: 7 }, (_, index) => ({
  label: (index + 1).toString(),
  value: (index + 1) * 60 * 60 * 24,
}));

const getHourOptions = (isScheduledForToday = false) => {
  const hourOptions = timeIntervalsMaker(TIME_DROPDOWN_INTERVAL);
  if (isScheduledForToday) {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const newHourOptions = hourOptions.filter((option) => currentMinutes <= Number(option.value));
    return newHourOptions;
  }
  return hourOptions;
};
interface Props {
  isReadOnly: boolean;
}

export const SettingsFieldset = ({ isReadOnly }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'broadcasts', permissionsActions.WRITE)
  );

  const broadcast = useSelector(selectBroadcast);
  const [showSessionTimeout, setShowSessionTimeout] = useState(false);

  const { setValue, control } = useFormContext<Form>();
  const [stepStatus, setStepStatus] = useState<FieldsetStatus>('disabled');
  const scheduleDate = useWatch({ control, name: 'scheduleDate' });
  const scheduleDateExists = scheduleDate && scheduleDate !== SEND_NOW;
  const scheduleHour = useWatch({ control, name: 'scheduleHour' });
  const template = useWatch({ control, name: 'template' });
  const calendarRef = useRef(null);
  const [calendarState, setCalendarState] = useState(
    scheduleDateExists ? new Date(scheduleDate) : new Date()
  );
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (broadcast?.session_timeout) {
      setShowSessionTimeout(true);
    }
  }, [broadcast?.session_timeout]);

  useEffect(() => {
    if (template && stepStatus !== 'completed') {
      setStepStatus('active');
    }
    if (template && scheduleDate) {
      setStepStatus('completed');
    }
  }, [scheduleDate, stepStatus, template]);

  const handleCalendarClose = () => {
    setValue('scheduleDate', format(calendarState, 'yyyy-MM-dd'));
    setShowCalendar(false);
  };

  useClickAway(calendarRef, async () => {
    await delay(200);
    if (showCalendar) {
      handleCalendarClose();
    }
  });

  const options = periodOptions.map((option) => ({
    label: t(option.title_key),
    value: !isNil(option?.days) ? String(option.days) : null,
  }));
  const handleDateClick = (data: OptionBase) => {
    if (data.label === t(periodOptions[0].title_key)) {
      trackEvent(EventName.ClickBroadCastSendNow);
      setValue('scheduleDate', SEND_NOW);
      return;
    }
    trackEvent(EventName.ClickBroadCastSchedule);

    if (isNil(data.value)) {
      setShowCalendar((prev) => !prev);
      return;
    }
    const date = format(addDays(new Date(), Number(data.value)), 'yyyy-MM-dd');
    setValue('scheduleDate', date);
  };

  const handleSingleDayClick = (data: Date | undefined) => {
    if (data) {
      setCalendarState(data);
      setValue('scheduleDate', format(data, 'yyyy-MM-dd'));
      setShowCalendar(false);
    }
  };

  const handleHourClick = (data) => {
    const hour = getHourOptions().find((option) => option.value === data.value);
    setValue('scheduleHour', hour.label);
  };

  const handleBrainSubmit = (
    selection: { brain_parent_id: string; brain_version: number } | null
  ) => {
    if (!selection) {
      return;
    }
    dispatch(
      actions.updateBroadcast({
        brain_parent_id: selection.brain_parent_id,
        brain_version: selection.brain_version,
      })
    );
  };

  const handleSessionTimeoutSelect = useCallback(() => {
    if (!showSessionTimeout) {
      setValue('session_timeout', optionsSessionTimeout[6].value);
    } else {
      setValue('session_timeout', null);
    }
    setShowSessionTimeout((prev) => !prev);
  }, [setValue, showSessionTimeout]);

  const isScheduledForToday = scheduleDate === format(new Date(), 'yyyy-MM-dd');
  const isDisabled = !canWrite || stepStatus === 'disabled';
  const isHourSelectionDisabled = scheduleDate === SEND_NOW || isDisabled;

  return (
    <>
      <StepFieldset
        title={t('common.settings')}
        step={3}
        status={isReadOnly ? 'completed' : stepStatus}
      >
        <TitleSubtitle
          title={t('broadcasts.schedule')}
          subtitle={t('broadcasts.select_schedule')}
          size="large"
        />

        <div className={styles.dateContainer}>
          <div>
            <DropdownList
              options={options}
              size="auto"
              height="medium"
              optionClick={handleDateClick}
              disabled={isDisabled || isReadOnly}
              sortBy={false}
            >
              <div className={styles.buttonContainer}>
                <CalendarIcon size={16} className="text-foreground-muted" />
                {!scheduleDate ? <>{t('broadcasts.select_date')}</> : <>{scheduleDate}</>}
              </div>
            </DropdownList>
            <Popover open={showCalendar} onOpenChange={setShowCalendar}>
              <PopoverTrigger />
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={calendarState}
                  onSelect={handleSingleDayClick}
                  disabled={(date) => isBefore(date, new Date())}
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className={styles.hourPicker}>
            <DropdownList
              optionClick={handleHourClick}
              options={getHourOptions(isScheduledForToday)}
              height="medium"
              noGutter
              size="auto"
              disabled={isHourSelectionDisabled || isReadOnly}
              sortBy={false}
            >
              <div className={styles.buttonContainer}>
                <ClockIcon size={14} className="text-foreground-muted" />
                {!scheduleHour ? <>{t('common.select')}</> : <>{scheduleHour}</>}
              </div>
            </DropdownList>
          </div>
        </div>

        <div className="mt-4">
          <TitleSubtitle
            title={t('broadcasts.ai_agent')}
            subtitle={t('broadcasts.select_ai_agent')}
            size="large"
          />
          <div className={styles.brainContainer}>
            <BrainVersionSelector
              onSelect={handleBrainSubmit}
              initialBrainId={broadcast?.brain_parent_id}
              initialVersion={broadcast?.brain_version}
              disabled={isDisabled || isReadOnly}
            />
          </div>
        </div>
        <div className={styles.switchColumn}>
          <Controller
            name="force_new_sessions"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Switch
                checked={value}
                onCheckedChange={onChange}
                disabled={stepStatus === 'disabled' || isReadOnly}
              />
            )}
          />
          <TitleSubtitle
            title={t('broadcasts.new_conversation')}
            subtitle={t('broadcasts.new_conversation_subtitle')}
          />
        </div>
        <div>
          <div className={styles.switchColumn}>
            <Switch
              checked={showSessionTimeout}
              onCheckedChange={handleSessionTimeoutSelect}
              disabled={stepStatus === 'disabled' || isReadOnly}
            />
            <TitleSubtitle
              title={t('conversations.expiration_time')}
              subtitle={t('conversations.expiration_time_subtitle')}
            />
          </div>
          {showSessionTimeout && (
            <div className={styles.switchColumn}>
              <FormField
                control={control}
                name="session_timeout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('broadcasts.days')}</FormLabel>
                    <Select
                      onValueChange={(val) => field.onChange(Number(val))}
                      value={field.value?.toString()}
                      disabled={stepStatus === 'disabled' || isReadOnly}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('common.select')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {optionsSessionTimeout.map((option) => (
                          <SelectItem key={option.value} value={option.value.toString()}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
        </div>
      </StepFieldset>
    </>
  );
};
